import React, { FC, RefObject, useState } from "react";

import { Button, TextField } from "@telia/teddy";
import _get from "lodash/get";
import Autosuggest, { BlurEvent, ChangeEvent } from "react-autosuggest";
import { connect, useDispatch } from "react-redux";
import { FORM_ID } from "../../../../../../constants";
import { DeliveryAddressSuggestion, Form, RootState } from "../../../../../../models";
import { trackFunnelEvent } from "../../../../google-analytics";
import RapidForm from "../../../../rapidform/components/molecules/RapidForm";
import { findDeliveryAddress } from "../../../state/actions/deliveryInfoActions";
import { getFormById } from "../../../state/selectors/formSelectors";
import TEXT from "../../../text-consts";
import "./EditDeliveryAddressForm.less";

interface EditDeliveryAddressProps {
    deliveryAddressForm: Form;
    initialValue?: string;
    onSubmitDeliveryAddressForm: (selectedAddress: DeliveryAddressSuggestion) => void;
}

interface InputProps<TSuggestion> {
    address: string;
    id: string;
    onBlur?: (event: React.FocusEvent, params?: BlurEvent<TSuggestion>) => void;
    onChange: (event: React.FormEvent, newValue: ChangeEvent) => void;
    ref: RefObject<HTMLInputElement>;
    value?: string;
}

interface StateProps {
    form: Form;
}

const formatAddress = (address: DeliveryAddressSuggestion): string => {
    let adressString = "" + address.streetName ? address.streetName : "";
    adressString += address.streetNumber ? " " + address.streetNumber : "";
    adressString += address.streetNumberSuffix ? " " + address.streetNumberSuffix : "";
    adressString += address.postcode ? ", " + address.postcode : "";
    adressString += address.city ? " " + address.city : "";

    return adressString;
};

const EditDeliveryAddressForm: FC<EditDeliveryAddressProps> = ({
    initialValue = "",
    deliveryAddressForm,
    onSubmitDeliveryAddressForm,
}) => {
    const [address, setAddress] = useState(initialValue);
    const onChangeFn = (_event: React.FormEvent, { newValue }: ChangeEvent): React.SetStateAction<void> => {
        setAddress(newValue);
    };
    const [validAddresses, setValidAddresses] = useState<DeliveryAddressSuggestion[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<DeliveryAddressSuggestion>();

    const dispatch = useDispatch();

    const onSuggestionsUpdateRequested = async ({ value: newValue }: { value: string }): Promise<void> => {
        try {
            const inputValue = newValue.trim().toLowerCase();
            const inputLength = inputValue.length;
            if (inputLength > 4) {
                const addressResult = await dispatch(findDeliveryAddress(newValue));
                const payloadAddresses: DeliveryAddressSuggestion[] = _get(addressResult, "action.payload", []);
                setValidAddresses(payloadAddresses);
            }
        } catch {
            // Something went wrong with call? just continue...
        }
    };

    const renderInputComponent = ({
        ref,
        onChange,
        ...inputProps
    }: InputProps<DeliveryAddressSuggestion>): React.ReactNode => {
        return (
            <TextField>
                <TextField.Input
                    placeholder="Angi leveringsadresse her"
                    {...inputProps}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        onChange(event, { newValue: event.target.value, method: "type" })
                    }
                    ref={ref}
                    data-di-mask
                />
            </TextField>
        );
    };

    const onSuggestionSelected = (
        _event: React.FormEvent,
        { suggestion }: { suggestion: DeliveryAddressSuggestion },
    ): void => {
        setSelectedAddress(suggestion);
        trackFunnelEvent({
            name: "Mobil abo",
            isUIEvent: true,
            uiEventName: "change_address_ok",
        });
    };

    const getSuggestionValue = (suggestion: DeliveryAddressSuggestion): string => {
        return formatAddress(suggestion);
    };

    const renderSuggestion = (suggestion: DeliveryAddressSuggestion): React.ReactNode => {
        return (
            <div className="edit-delivery-address-container__suggestion">
                <span>{formatAddress(suggestion)}</span>
            </div>
        );
    };

    const onSubmitSelectedAddress = async (): Promise<void> => {
        if (selectedAddress) onSubmitDeliveryAddressForm(selectedAddress);
    };

    const inputProps = {
        value: address || "",
        onChange: onChangeFn,
        address: "address",
        id: "address",
    };

    return (
        <RapidForm
            id={FORM_ID.EDIT_DELIVERY_ADDRESS}
            action={"#"}
            errors={deliveryAddressForm.errors}
            values={deliveryAddressForm.values}
            visible={deliveryAddressForm.visible}
            started={deliveryAddressForm.started}
            onSubmit={onSubmitSelectedAddress}
        >
            <div className="edit-delivery-address-container">
                <Autosuggest
                    suggestions={validAddresses}
                    onSuggestionsFetchRequested={onSuggestionsUpdateRequested}
                    getSuggestionValue={getSuggestionValue}
                    renderSuggestion={renderSuggestion}
                    // @ts-expect-error nested 3 party error, fix later
                    renderInputComponent={renderInputComponent}
                    onSuggestionSelected={onSuggestionSelected}
                    onSuggestionsClearRequested={(): void => undefined}
                    inputProps={inputProps}
                    data-di-mask
                />
            </div>
            <Button id="edit-delivery-address-form-button" form={FORM_ID.EDIT_DELIVERY_ADDRESS} type="submit">
                {TEXT.CONTACT_INFO.UPDATE_DELIVERY_ADDRESS}
            </Button>
        </RapidForm>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    form: getFormById(state, FORM_ID.EDIT_DELIVERY_ADDRESS),
});

export default connect(mapStateToProps)(EditDeliveryAddressForm);
